/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseIc } from '../console/icons';

interface SettingsDialogProps {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg';
}

/** Small modal used by the access-key flows (create/add/generate/confirm). */
const SettingsDialog: React.FC<SettingsDialogProps> = ({ title, onClose, children, size = 'lg' }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        className={`ps-card w-full ${size === 'md' ? 'max-w-md' : 'max-w-lg'} max-h-[90vh] overflow-y-auto shadow-xl`}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ps-border">
          <h3 className="m-0 text-base font-medium text-ps-text">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
            aria-label="Close"
          >
            <CloseIc />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default SettingsDialog;
